import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { pageIdFromPath, type PageLayoutsMap, type PageLayoutConfig } from "@/lib/page-layouts";

const Home = lazy(() => import("@/pages/home"));

const BASE_URL = (import.meta as any).env?.BASE_URL || "/";

let cached: PageLayoutsMap | null = null;
let inflight: Promise<PageLayoutsMap> | null = null;

function loadLayouts(): Promise<PageLayoutsMap> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch(`${BASE_URL}api/theme/settings`, { credentials: "include" })
    .then((r) => (r.ok ? r.json() : null))
    .then((d: any) => {
      const m = (d?.pageLayouts || {}) as PageLayoutsMap;
      cached = m;
      return m;
    })
    .catch(() => ({} as PageLayoutsMap))
    .finally(() => { inflight = null; });
  return inflight;
}

export function invalidatePageLayoutsCache() {
  cached = null;
}

function SafeHtml({ html }: { html: string }) {
  if (!html) return null;
  return <div className="page-custom-html" dangerouslySetInnerHTML={{ __html: html }} />;
}

export function PageCustomization({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [layouts, setLayouts] = useState<PageLayoutsMap>(cached || {});
  useEffect(() => {
    let cancelled = false;
    loadLayouts().then((m) => { if (!cancelled) setLayouts(m); });
    return () => { cancelled = true; };
  }, []);
  const pageId = pageIdFromPath(location || "/");
  const cfg: PageLayoutConfig = (pageId && layouts[pageId]) || {};
  const enabled = cfg.enabled !== false;
  // Render block-based sections for any non-home system page.
  // The homepage already renders its own sections via the Home component
  // mounted on "/" — so we skip "home" here to avoid double-rendering.
  const showBlocks = !!pageId && pageId !== "home" && enabled;
  return (
    <>
      {enabled && cfg.customCss ? <style data-page-css={pageId}>{cfg.customCss}</style> : null}
      {enabled && cfg.topHtml ? <SafeHtml html={cfg.topHtml} /> : null}
      {showBlocks ? (
        <Suspense fallback={null}>
          <Home pageKey={pageId} />
        </Suspense>
      ) : null}
      {children}
      {enabled && cfg.bottomHtml ? <SafeHtml html={cfg.bottomHtml} /> : null}
    </>
  );
}
